/**
 * useOrderDetails Hook
 * React Query hook for fetching and managing order details with caching
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { fetchOrderDetails, type OrderDetails } from '@/api/orderDetails'

// Cache keys
const ORDER_DETAILS_KEY = 'order-details'

interface UseOrderDetailsOptions {
  /** Disable automatic fetching */
  enabled?: boolean
  /** Stale time in ms (default: 30 seconds) */
  staleTime?: number
}

interface UseOrderDetailsReturn {
  order: OrderDetails | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Hook for fetching order details with caching and prefetch support
 */
export function useOrderDetails(
  orderId: string | undefined,
  options: UseOrderDetailsOptions = {}
): UseOrderDetailsReturn {
  const {
    enabled = true,
    staleTime = 30 * 1000, // 30 seconds
  } = options

  const query = useQuery({
    queryKey: [ORDER_DETAILS_KEY, orderId],
    queryFn: () => fetchOrderDetails(orderId!),
    enabled: enabled && !!orderId,
    staleTime,
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    retry: 2,
  })

  return {
    order: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook for prefetching order details on hover
 */
export function usePrefetchOrderDetails() {
  const queryClient = useQueryClient()

  const prefetch = useCallback(
    (orderId: string) => {
      queryClient.prefetchQuery({
        queryKey: [ORDER_DETAILS_KEY, orderId],
        queryFn: () => fetchOrderDetails(orderId),
        staleTime: 30 * 1000,
      })
    },
    [queryClient]
  )

  return prefetch
}

/**
 * Hook for invalidating order details cache
 */
export function useInvalidateOrderDetails() {
  const queryClient = useQueryClient()

  const invalidate = useCallback(
    (orderId?: string) => {
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: [ORDER_DETAILS_KEY, orderId] })
      } else {
        queryClient.invalidateQueries({ queryKey: [ORDER_DETAILS_KEY] })
      }
    },
    [queryClient]
  )

  return invalidate
}

export type { OrderDetails }
